import React from 'react';
import { Typography, Box, Avatar, Chip } from '@mui/material';
import { Circle as CircleIcon, Campaign as CampaignIcon, EventRepeat as ChangeIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : null);

/**
 * ntype 'call_update' — payload.kind:
 *   'new_calls'    { count, calls: [{id, code, title, closes_at, program}] }
 *   'calls_changed' { count, updates: [{change_label, call_id, call_code, call_title, summary}] }
 *                   — every update from one approval, one notification (and email) per user
 *   'call_changed' { change_kind, change_label, call_id, call_code, call_title, summary } (older)
 */
export default function CallUpdateNotificationCard({ notification, onMarkRead }) {
  const navigate = useNavigate();
  const payload = notification.payload || {};
  const isNew = payload.kind === 'new_calls';
  const calls = payload.calls || [];
  const updates = payload.kind === 'calls_changed' ? (payload.updates || []) : null;
  const updateCallIds = [...new Set((updates || []).map((u) => u.call_id))];

  const open = (path) => (e) => {
    e?.stopPropagation?.();
    if (!notification.is_read && onMarkRead) onMarkRead();
    navigate(path);
  };
  const handleClick = isNew
    ? open(calls.length === 1 ? `/funding/call/${calls[0].id}/` : '/funding/')
    : updates
      ? open(updateCallIds.length === 1 ? `/funding/call/${updateCallIds[0]}/` : '/funding/')
      : open(`/funding/call/${payload.call_id}/`);

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: 'flex', alignItems: 'flex-start', gap: 1.5,
        py: 1, px: 2,
        borderBottom: '1px solid', borderColor: 'divider',
        backgroundColor: notification.is_read ? 'transparent' : 'action.hover',
        cursor: 'pointer',
        '&:hover': { backgroundColor: 'action.selected' },
      }}
    >
      <Avatar sx={{ width: 32, height: 32, bgcolor: '#0891b2', color: 'white', mt: 0.25 }}>
        {isNew ? <CampaignIcon fontSize="small" /> : <ChangeIcon fontSize="small" />}
      </Avatar>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {isNew ? (
          <>
            <Typography variant="body2" fontWeight={600}>
              {payload.message || `${payload.count} new funding calls match your profile`}
            </Typography>
            {calls.slice(0, 3).map((c) => (
              <Typography
                key={c.id}
                variant="caption"
                color="text.secondary"
                component="div"
                noWrap
                onClick={open(`/funding/call/${c.id}/`)}
                sx={{ '&:hover': { color: 'primary.main', textDecoration: 'underline' } }}
              >
                • {c.title}{c.closes_at ? ` — deadline ${fmtDate(c.closes_at)}` : ''}
              </Typography>
            ))}
            {payload.count > 3 && (
              <Typography variant="caption" color="primary.main">+ {payload.count - 3} more</Typography>
            )}
          </>
        ) : updates ? (
          <>
            <Typography variant="body2" fontWeight={600}>
              {updates.length === 1 ? (updates[0].change_label || 'Call updated') : `${updates.length} updates to calls you follow`}
            </Typography>
            {updates.slice(0, 4).map((u, i) => (
              <Typography
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                variant="caption"
                color="text.secondary"
                component="div"
                noWrap
                onClick={open(`/funding/call/${u.call_id}/`)}
                sx={{ '&:hover': { color: 'primary.main', textDecoration: 'underline' } }}
              >
                • {u.call_code}: {u.summary}
              </Typography>
            ))}
            {updates.length > 4 && (
              <Typography variant="caption" color="primary.main">+ {updates.length - 4} more</Typography>
            )}
          </>
        ) : (
          <>
            <Typography variant="body2" sx={{ flexWrap: 'wrap' }}>
              <Box component="span" fontWeight={600}>{payload.change_label || 'Call updated'}</Box>
              <Box component="span" color="text.secondary"> — {payload.call_title || payload.call_code}</Box>
            </Typography>
            {payload.summary && (
              <Typography variant="caption" color="text.secondary" component="div" sx={{ wordBreak: 'break-word' }}>
                {payload.summary.length > 220 ? `${payload.summary.slice(0, 220)}…` : payload.summary}
              </Typography>
            )}
            {payload.call_code && (
              <Chip label={payload.call_code} size="small" variant="outlined" sx={{ mt: 0.5, height: 18, fontSize: '0.65rem' }} />
            )}
          </>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {notification.created ? formatDistanceToNow(new Date(notification.created), { addSuffix: true }) : ''}
        </Typography>
        {!notification.is_read && <CircleIcon sx={{ fontSize: 8, color: 'primary.main' }} />}
      </Box>
    </Box>
  );
}
